<template>
    <!-- Password change modal -->
    <b-modal
        v-model="isVisible"
        size="lg"
        title="Change password"
        modal-class="vue-need-bootstrap"
        lazy
        centered
        hide-footer
    >
        <template #default="{ cancel }">
            <div v-if="!passwordChanged">
                <validation-observer v-slot="{ handleSubmit, valid }">
                    <b-form @submit.stop.prevent="handleSubmit(changePassword)">
                        <!-- Current Password -->
                        <validation-provider v-slot="{ valid, errors }" rules="required|min:8" name="Current password">
                            <b-form-group
                                label-cols="3"
                                content-cols="9"
                                label="Current password"
                                label-for="current-password-input"
                                class="my-3"
                                :invalid-feedback="invalidPassword ? 'Wrong password' : errors[0]"
                                :state="invalidPassword ? false : errors[0] ? false : valid ? true : null"
                            >
                                <b-form-input
                                    v-model="currentPassword"
                                    type="password"
                                    :state="invalidPassword ? false : errors[0] ? false : valid ? true : null"
                                    :disabled="savingPassword"
                                />
                            </b-form-group>
                        </validation-provider>

                        <!-- New Password -->
                        <validation-provider
                            v-slot="{ valid, errors }"
                            rules="required|min:8"
                            name="Password"
                            vid="newPassword"
                        >
                            <b-form-group
                                id="new-password-input"
                                label-cols="3"
                                content-cols="9"
                                label="New password"
                                label-for="new-password-input"
                                class="my-3"
                                :invalid-feedback="errors[0]"
                                :state="errors[0] ? false : valid ? true : null"
                            >
                                <b-form-input
                                    v-model="newPassword"
                                    type="password"
                                    :state="errors[0] ? false : valid ? true : null"
                                    :disabled="savingPassword"
                                />
                            </b-form-group>
                        </validation-provider>

                        <!-- Confirm New Password -->
                        <validation-provider
                            v-slot="{ valid, errors }"
                            rules="required|min:8|password-confirmed:newPassword"
                            name="Password"
                        >
                            <b-form-group
                                id="confirm-new-password-input"
                                label-cols="3"
                                content-cols="9"
                                label="Confirm new password"
                                label-for="confirm-new-password-input"
                                class="my-3"
                                :invalid-feedback="errors[0]"
                                :state="errors[0] ? false : valid ? true : null"
                            >
                                <b-form-input
                                    v-model="confirmNewPassword"
                                    type="password"
                                    :state="errors[0] ? false : valid ? true : null"
                                    :disabled="savingPassword"
                                />
                            </b-form-group>
                        </validation-provider>

                        <div class="error-message w-100 pb-3 text-center">
                            {{ errorMessage }}
                        </div>

                        <div class="modal-footer row pb-0">
                            <b-button type="reset" :disabled="savingPassword" @click="cancel()"> Cancel </b-button>
                            <loader-btn
                                variant="primary"
                                type="submit"
                                loading-text="Saving"
                                :loading="savingPassword"
                                :disabled="!valid || savingPassword"
                            >
                                Update password
                            </loader-btn>
                        </div>
                    </b-form>
                </validation-observer>
            </div>

            <!-- Password updated confirmation -->
            <template v-else>
                <div class="align-items-center confirmation-message justify-content-center">
                    <div>Password successfully changed!</div>
                </div>
                <div class="modal-footer row pb-0">
                    <b-button variant="primary" @click="onPasswordChanged()"> Continue </b-button>
                </div>
            </template>
        </template>
    </b-modal>
</template>

<script lang="ts" setup>
import { Ref, computed, ref } from 'vue';
import { AxiosError } from 'axios';
import { ValidationProvider, ValidationObserver } from 'vee-validate';
import { changePassword as _changePassword } from '@/api/user';
import { BModal } from 'bootstrap-vue';
import LoaderBtn from '@/components/LoaderBtn/LoaderBtn.vue';

const emit = defineEmits(['input', 'password-changed']);

const props = defineProps<{
    value: boolean;
}>();

const currentPassword = ref('');
const newPassword = ref('');
const confirmNewPassword = ref('');

const savingPassword = ref(false);
const passwordChanged = ref(false);
const invalidPassword = ref(false);
const errorMessage: Ref<string | null> = ref(null);

const isVisible = computed({
    get(): boolean {
        return props.value;
    },
    set(value: boolean) {
        if (value) {
            errorMessage.value = null;
            passwordChanged.value = false;
            currentPassword.value = '';
            newPassword.value = '';
            confirmNewPassword.value = '';
        }
        emit('input', value);
    }
});

const changePassword = async () => {
    try {
        savingPassword.value = true;
        errorMessage.value = null;

        if (currentPassword.value && newPassword.value && confirmNewPassword.value) {
            await _changePassword(currentPassword.value, newPassword.value, confirmNewPassword.value);
        }
        passwordChanged.value = true;
    } catch (error) {
        const axiosError = error as AxiosError;
        if (axiosError.response?.status === 400) {
            if (axiosError.response.data.Message) {
                if (axiosError.response.data.Message === 'Invalid Password') {
                    invalidPassword.value = true;
                } else {
                    errorMessage.value = axiosError.response.data.Message;
                }
            }
        } else {
            errorMessage.value = 'Fatal error';
            window.rumReportError(axiosError);
        }
    } finally {
        savingPassword.value = false;
    }
};

const onPasswordChanged = () => {
    emit('password-changed');
};
</script>

<style lang="scss" scoped>
.confirmation-message {
    display: flex;
    min-height: 200px;
}
</style>
